import { useGetAvailablePrescribers } from '@cannect/services/resources/scheduling'
import { useAvailableTimestamps } from '@cannect/hooks/useAvailableTimestamps'
import { cn } from '@cannect/lib/utils'
import DOMPurify from 'dompurify'
import { format, isSameDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Check, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent, type PointerEvent } from 'react'
import { PrescriberAvatar, TrailHeading } from './screens'

// A biografia vem do cadastro do prescritor com marcação HTML (<p>, <br>, <strong>...). O card limita o texto a 4
// linhas, então vira texto simples: sanitiza, troca quebras de bloco por espaço e decodifica as entidades.
const biographyText = (html: string) => {
  const clean = DOMPurify.sanitize(html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, ' '), { ALLOWED_TAGS: [] })
  const element = document.createElement('textarea')
  element.innerHTML = clean
  return element.value.replace(/\s+/g, ' ').trim()
}

// Arrastar com o mouse rola a faixa de dias (no toque o scroll já é nativo). Depois de arrastar, o clique de soltar não
// seleciona o dia em que o ponteiro parou.
const useDragScroll = () => {
  const ref = useRef<HTMLDivElement>(null)
  const drag = useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 })

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !ref.current) return
    drag.current = { active: true, moved: false, startX: event.clientX, scrollLeft: ref.current.scrollLeft }
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current
    if (!state.active || !ref.current) return
    const delta = event.clientX - state.startX
    if (!state.moved && Math.abs(delta) < 5) return
    if (!state.moved) {
      state.moved = true
      ref.current.setPointerCapture(event.pointerId)
    }
    ref.current.scrollLeft = state.scrollLeft - delta
  }

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    drag.current.active = false
    if (ref.current?.hasPointerCapture(event.pointerId)) ref.current.releasePointerCapture(event.pointerId)
  }

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!drag.current.moved) return
    drag.current.moved = false
    event.preventDefault()
    event.stopPropagation()
  }

  return {
    ref,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onClickCapture,
    onDragStart: (event: DragEvent) => event.preventDefault()
  }
}

const LOADING_MESSAGES = [
  'Buscando a agenda do seu médico…',
  'Conferindo os horários disponíveis…',
  'Quase pronto, organizando os dias…'
]

const bone = 'rounded-lg bg-[#ECECEC] motion-safe:animate-pulse'

// Enquanto a agenda carrega, mostra o mesmo desenho da tela final (médico, dias e horários) em esqueleto, com uma
// mensagem de progresso que muda: a página não "pula" quando os horários chegam e a espera parece menor.
const ScheduleSkeleton = () => {
  const [message, setMessage] = useState(0)

  useEffect(() => {
    const timer = window.setInterval(() => setMessage((current) => (current + 1) % LOADING_MESSAGES.length), 2600)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div className="flex flex-1 flex-col gap-5" role="status" aria-live="polite" aria-busy="true">
      <TrailHeading eyebrow="Agendamento" title="Escolha o dia e o horário da sua consulta" />

      <p className="m-0 inline-flex items-center gap-2 rounded-full bg-marketplace-100 px-3.5 py-2 text-[13px] font-medium text-marketplace-900">
        <Loader2 className="size-4 shrink-0 motion-safe:animate-spin" aria-hidden />
        {LOADING_MESSAGES[message]}
      </p>

      <section className="rounded-2xl border border-solid border-[#ECECEC] bg-white p-4" aria-hidden>
        <div className="flex items-center gap-3">
          <div className={cn(bone, 'size-12 shrink-0 rounded-full')} />
          <div className="flex flex-1 flex-col gap-2">
            <div className={cn(bone, 'h-3.5 w-3/5')} />
            <div className={cn(bone, 'h-3 w-2/5')} />
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <div className={cn(bone, 'h-3 w-full')} />
          <div className={cn(bone, 'h-3 w-11/12')} />
          <div className={cn(bone, 'h-3 w-3/5')} />
        </div>
      </section>

      <div className="flex flex-col gap-3" aria-hidden>
        <div className={cn(bone, 'h-4 w-36')} />
        <div className="-mx-1 flex gap-2 overflow-hidden px-1">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className={cn(bone, 'h-[60px] min-w-[52px] flex-1 rounded-xl')} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3" aria-hidden>
        <div className={cn(bone, 'h-4 w-48')} />
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 9 }, (_, index) => (
            <div key={index} className={cn(bone, 'h-11 rounded-xl')} />
          ))}
        </div>
      </div>
    </div>
  )
}

interface ScheduleScreenProps {
  onSelect: (payload: {
    prescriberId: number
    prescriberUserId?: number
    duration?: number
    name: string
    date: string
    hour: string
    label: string
  }) => void
  selectedKey?: string
  // Prescritor da consulta inclusa, definido na API (WEIGHT_LOSS_PROGRAM_PRESCRIBER_ID) e enviado na etapa de agendamento.
  programPrescriberId?: number | null
}

export const ScheduleScreen = ({ onSelect, selectedKey, programPrescriberId }: ScheduleScreenProps) => {
  const prescriberId = Number(programPrescriberId || 0)
  const { data, isLoading } = useGetAvailablePrescribers({
    filterBy: prescriberId ? { type: 'prescribers_ids', value: JSON.stringify([prescriberId]) } : undefined,
    sortBy: 'available_date_times',
    orderBy: 'asc'
  })

  const prescriber = data?.prescribers?.find((item) => Number(item.id) === prescriberId)
  const timestamps = prescriber?.available_date_times || []
  const { isDateDisabled, getAvailableTimesForDate } = useAvailableTimestamps(timestamps)
  const [day, setDay] = useState<Date | null>(null)
  const dragScroll = useDragScroll()

  const availableDays = useMemo(() => {
    const unique: Date[] = []
    timestamps.forEach((stamp) => {
      const date = new Date(stamp)
      date.setHours(0, 0, 0, 0)
      if (!unique.some((item) => item.getTime() === date.getTime()) && !isDateDisabled(date)) {
        unique.push(date)
      }
    })
    return unique.sort((first, second) => first.getTime() - second.getTime())
  }, [timestamps, isDateDisabled])

  const activeDay = day || availableDays[0]
  const slots = activeDay ? getAvailableTimesForDate(activeDay) : []

  if (isLoading) return <ScheduleSkeleton />

  if (!prescriberId) {
    return <p className="text-sm text-[#6B6560]">A agenda do programa ainda não está configurada.</p>
  }

  if (!prescriber) {
    return (
      <p className="text-sm text-[#6B6560]">
        Não encontramos horários agora. Tente novamente em instantes ou fale com o atendimento.
      </p>
    )
  }

  const monthLabel = activeDay ? format(activeDay, "MMMM 'de' yyyy", { locale: ptBR }) : ''
  // Abreviação de 3 letras ('Seg', 'Ter'), como no design.
  const weekday = (date: Date) => {
    const name = format(date, 'EEEE', { locale: ptBR }).slice(0, 3)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  return (
    // No desktop: card de agenda (título, dias, horários) à esquerda e o médico numa lateral fixa à direita. O fundo do
    // card é um elemento próprio atrás das 3 linhas da coluna, para o médico seguir entre título e dias no mobile.
    <div className="flex flex-1 flex-col gap-5 md:grid md:flex-none md:grid-cols-[minmax(0,1fr)_300px] md:items-start md:gap-x-6 md:gap-y-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div
        aria-hidden
        className="hidden self-stretch rounded-2xl border border-solid border-[#ECECEC] bg-white shadow-[0_8px_24px_rgba(42,47,29,0.06)] md:col-start-1 md:row-span-3 md:row-start-1 md:block"
      />
      <div className="md:relative md:col-start-1 md:row-start-1 md:px-8 md:pt-8">
        <TrailHeading eyebrow="Agendamento" title="Escolha o dia e o horário da sua consulta" />
      </div>
      <section className="rounded-2xl border border-solid border-[#ECECEC] bg-white p-4 md:sticky md:top-0 md:col-start-2 md:row-span-3 md:row-start-1 md:p-5">
        <p className="m-0 mb-3 hidden text-xs font-semibold text-marketplace-800 md:block">Seu médico</p>
        <div className="flex items-center gap-3">
          <PrescriberAvatar name={prescriber.professional_name} />
          <div>
            <p className="m-0 text-sm font-semibold text-[#1A1A1A]">{prescriber.professional_name}</p>
            <p className="m-0 text-xs text-[#6B6560]">
              {prescriber.council}-{prescriber.uf_council} {prescriber.nr_council}
            </p>
            {prescriber.prescriber_expertises?.length ? (
              <p className="m-0 text-xs text-[#6B6560]">
                {prescriber.prescriber_expertises.map((item) => item.name).join(', ')}
              </p>
            ) : null}
          </div>
        </div>
        {prescriber.biography ? (
          <p className="m-0 mt-3 line-clamp-4 text-xs leading-snug text-[#433F3E] md:line-clamp-6 md:text-[13px] md:leading-[1.5]">
            {biographyText(prescriber.biography)}
          </p>
        ) : null}
        <hr className="my-3 border-0 border-t border-solid border-[#EEEEEE]" />
        <p className="m-0 inline-flex items-center gap-1.5 text-xs text-[#433F3E]">
          <Check className="size-3.5 text-marketplace-800" />
          Consulta médica inclusa no programa · pagamento confirmado
        </p>
      </section>

      <div className="flex flex-col gap-3 md:relative md:col-start-1 md:row-start-2 md:px-8">
        <p className="m-0 text-sm font-semibold text-[#1A1A1A] first-letter:uppercase md:text-base">{monthLabel}</p>
        <div
          {...dragScroll}
          className="-mx-1 flex select-none gap-2 overflow-x-auto px-1 pb-1 md:cursor-grab md:gap-3 md:active:cursor-grabbing">
          {availableDays.map((item) => {
            const selected = activeDay && isSameDay(item, activeDay)
            return (
              <button
                key={item.toISOString()}
                type="button"
                onClick={() => setDay(item)}
                className={cn(
                  'flex min-w-[52px] flex-1 flex-col items-center gap-0.5 rounded-xl border border-solid py-2.5 md:min-w-[72px] md:gap-1 md:py-3.5',
                  selected
                    ? 'border-marketplace-800 bg-marketplace-800 text-white'
                    : 'border-[#E8E8E8] bg-white text-[#1A1A1A]'
                )}>
                <span className={cn('text-[11px] md:text-[13px]', selected ? 'text-white/85' : 'text-[#6B6560]')}>
                  {weekday(item)}
                </span>
                <span className="text-lg font-bold leading-none md:text-xl">{format(item, 'd')}</span>
              </button>
            )
          })}
        </div>
      </div>

      {activeDay ? (
        <div className="flex flex-col gap-3 md:relative md:col-start-1 md:row-start-3 md:px-8 md:pb-8">
          <p className="m-0 text-sm font-semibold text-[#1A1A1A] md:text-base">
            Horários disponíveis · {weekday(activeDay)}, {format(activeDay, 'd')}
          </p>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-4 md:gap-3 lg:grid-cols-6">
            {slots.map((slot) => {
              const date = format(activeDay, 'yyyy-MM-dd')
              const key = `${prescriber.id}-${date}-${slot.value}`
              const active = selectedKey === key
              return (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() =>
                    onSelect({
                      prescriberId: prescriber.id,
                      prescriberUserId: prescriber.user?.id,
                      duration: prescriber.appointment_duration,
                      name: prescriber.professional_name,
                      date,
                      hour: slot.value,
                      label: `Consulta médica, ${weekday(activeDay)}, ${format(activeDay, "d 'de' MMMM", { locale: ptBR })}, ${slot.label}`
                    })
                  }
                  className={cn(
                    'rounded-xl border border-solid py-3 text-sm md:rounded-full md:hover:border-marketplace-800',
                    active
                      ? 'border-marketplace-800 bg-marketplace-200 font-semibold text-[#1A1A1A]'
                      : 'border-[#E8E8E8] bg-white text-[#1A1A1A]'
                  )}>
                  {slot.label}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
